/**
 * Utility for converting File objects to base64 strings for JSON transmission
 * This allows us to send files as part of JSON payloads instead of using FormData
 */

export interface FileData {
  name: string
  size: number
  type: string
  data: string // base64 encoded file content
}

/**
 * Convert a File object to base64 string with metadata
 */
export const convertFileToBase64 = async (file: File): Promise<FileData> => {
  return new Promise<FileData>((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      const result = reader.result as string
      // Remove the data URL prefix (e.g., "data:text/csv;base64,")
      const base64Data = result.split(',')[1]

      resolve({
        name: file.name,
        size: file.size,
        type: file.type,
        data: base64Data,
      })
    }

    reader.onerror = () => {
      reject(new Error(`Failed to convert file ${file.name} to base64`))
    }

    reader.readAsDataURL(file)
  })
}

/**
 * Convert all File objects in a data object to base64 FileData objects
 * This function recursively processes the data object and converts any File instances
 */
export const convertFilesToBase64 = async (data: any): Promise<any> => {
  if (!data || typeof data !== 'object') {
    return data
  }

  // Handle arrays
  if (Array.isArray(data)) {
    const convertedArray = []
    for (const item of data) {
      convertedArray.push(await convertFilesToBase64(item))
    }
    return convertedArray
  }

  // Handle objects
  const convertedData = { ...data }

  for (const [key, value] of Object.entries(data)) {
    if (value instanceof File) {
      // Convert File to base64 FileData
      convertedData[key] = await convertFileToBase64(value)
    } else if (value && typeof value === 'object') {
      // Recursively convert nested objects
      convertedData[key] = await convertFilesToBase64(value)
    }
  }

  return convertedData
}

/**
 * Check if a data object contains any File objects
 */
export const hasFiles = (data: any): boolean => {
  if (!data || typeof data !== 'object') {
    return false
  }

  if (Array.isArray(data)) {
    return data.some((item) => hasFiles(item))
  }

  return Object.values(data).some((value) => {
    if (value instanceof File) {
      return true
    }
    if (value && typeof value === 'object') {
      return hasFiles(value)
    }
    return false
  })
}
